import { useState, useCallback, useMemo } from 'react';
import { SortingState } from '@tanstack/react-table';
import { NetworkInsights, PostTypeInsights } from '@frontend/gql/social/graphql';
import { useGenericTable } from './useGenericTable';
import { NetworksTableColumn } from '../config/defaultNetworksTableColumns';

type PostTypeTableRow = PostTypeInsights;

interface NetworksData {
  client?: {
    socialAnalytics?: {
      networks?: NetworkInsights[];
      postTypes?: PostTypeInsights[];
    };
  };
}

interface UseNetworksTableConfig {
  data?: NetworksData;
  selectedColumns: NetworksTableColumn<PostTypeTableRow>[];
  setSelectedColumns: (columns: NetworksTableColumn<PostTypeTableRow>[]) => void;
  currentPage: number;
  pageSize: number;
  handleSortingChange: (sorting: SortingState) => void;
  sorting: SortingState;
  socialDataReconciliation?: boolean;
}

export const useNetworksTable = (config: UseNetworksTableConfig) => {
  const { tableProps } = useGenericTable<PostTypeTableRow>({
    data: config.data?.client?.socialAnalytics?.postTypes ?? [],
    selectedColumns: config.selectedColumns,
    setSelectedColumns: config.setSelectedColumns,
    currentPage: config.currentPage,
    pageSize: config.pageSize,
    globalFilter: '',
    setIsPartialLoading: () => { /* no-op for networks table */ },
    handleSortingChange: config.handleSortingChange,
    sorting: config.sorting,
  });

  const [selectedRows, setSelectedRows] = useState<PostTypeTableRow[]>([]);

  const onSelectionChange = useCallback((rows: PostTypeTableRow[]) => {
    setSelectedRows((prevRows) => {
      // Only update if the rows are actually different
      if (rows.length !== prevRows.length ||
          rows.some((row, index) => row !== prevRows[index])) {
        return rows;
      }
      return prevRows;
    });
  }, []);

  const metricsCardProps = useMemo(() => {
    const networksLookup = config.data?.client?.socialAnalytics?.networks?.reduce((acc, network) => {
      if (network?.network) {
        acc[network.network] = network;
      }
      return acc;
    }, {} as Record<string, NetworkInsights>) ?? {};

    return {
      instagram: {
        data: networksLookup.instagram ?? null,
        socialDataReconciliation: config.socialDataReconciliation
      },
      tiktok: {
        data: networksLookup.tiktok ?? null,
        socialDataReconciliation: config.socialDataReconciliation
      },
      youtube: {
        data: networksLookup.youtube ?? null,
        socialDataReconciliation: config.socialDataReconciliation
      },
      pinterest: {
        data: networksLookup.pinterest ?? null,
        socialDataReconciliation: config.socialDataReconciliation
      },
    };
  }, [config.data, config.socialDataReconciliation]);

  const postTypeTableProps = useMemo(() => ({
    ...tableProps,
    onSelectionChange,
    selectedRows,
    setSelectedColumns: config.setSelectedColumns,
    currentPage: config.currentPage,
    pageSize: config.pageSize,
    setIsPartialLoading: () => { /* no-op for networks table */ },
  }), [tableProps, onSelectionChange, selectedRows, config.setSelectedColumns, config.currentPage, config.pageSize]);

  return {
    postTypeTableProps,
    metricsCardProps,
  };
};
